import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addEquipmentPart,
  createChecklistRun,
  createChecklistTemplate,
  deleteChecklistRun,
  deleteChecklistTemplate,
  patchChecklistTemplate,
  createEquipment,
  createLog,
  deleteAsset,
  deleteEquipment,
  deleteEquipmentPart,
  deleteLog,
  fetchAsset,
  fetchAssets,
  fetchChecklistRun,
  fetchChecklistRuns,
  fetchChecklistTemplate,
  fetchChecklistTemplates,
  fetchDomain,
  fetchDomains,
  fetchConsumables,
  fetchEquipment,
  fetchEquipmentList,
  fetchKit,
  fetchKits,
  fetchLowStock,
  fetchTools,
  fetchProject,
  fetchProjects,
  createProject,
  patchProject,
  deleteProject,
  fetchVendors,
  createVendor,
  patchVendor,
  deleteVendor,
  fetchReferences,
  createReference,
  deleteReference,
  fetchGuide,
  fetchGuides,
  createGuide,
  patchGuideApi,
  deleteGuideApi,
  type CreateGuideBody,
  fetchHealth,
  fetchLog,
  fetchLogs,
  fetchZones,
  patchAsset,
  patchChecklistRun,
  patchChecklistRunItem,
  patchEquipment,
  patchEquipmentPart,
  patchLog,
  uploadAsset,
  type AssetsFilter,
  type ChecklistRunsFilter,
  addKitItemApi,
  createConsumable,
  createKit,
  createTool,
  deleteConsumable,
  deleteKit,
  deleteKitItem,
  deleteTool,
  patchConsumable,
  patchTool,
  type CreateConsumableBody,
  type CreateEquipmentBody,
  type CreateKitBody,
  type CreateProjectBody,
  type CreateReferenceBody,
  type CreateToolBody,
  type CreateVendorBody,
  type ProjectsFilter,
  type CreateLogBody,
  type CreatePartBody,
  type CreateRunBody,
  type CreateTemplateBody,
  type LogsFilter,
  type UploadAssetMeta,
} from './api';
import type {
  AssetWithTags,
  Consumable,
  Kit,
  KitItemRow,
  KitItemType,
  KitWithItems,
  Project,
  Reference,
  Tool,
  Vendor,
  ChecklistRun,
  ChecklistRunItem,
  ChecklistRunItemStatus,
  ChecklistRunStatus,
  ChecklistRunWithItems,
  EquipmentDetail,
  MaintenanceLog,
} from './types';

/**
 * Centralized TanStack Query hooks. Keeps query keys consistent and
 * gives us a single place to tune `staleTime` per resource if needed.
 */

export const useHealth = () => useQuery({ queryKey: ['health'], queryFn: fetchHealth });

export const useDomains = () =>
  useQuery({ queryKey: ['domains'], queryFn: fetchDomains, staleTime: 5 * 60_000 });

export const useDomain = (slug: string | undefined) =>
  useQuery({
    queryKey: ['domain', slug],
    queryFn: () => fetchDomain(slug!),
    enabled: !!slug,
    staleTime: 60_000,
  });

export const useZones = () =>
  useQuery({ queryKey: ['zones'], queryFn: fetchZones, staleTime: 5 * 60_000 });

export const useGuides = (domain?: string) =>
  useQuery({
    queryKey: ['guides', domain ?? 'all'],
    queryFn: () => fetchGuides(domain),
    staleTime: 5 * 60_000,
  });

export const useGuide = (idOrSlug: string | undefined) =>
  useQuery({
    queryKey: ['guide', idOrSlug],
    queryFn: () => fetchGuide(idOrSlug!),
    enabled: !!idOrSlug,
    staleTime: 5 * 60_000,
  });

export const useCreateGuide = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGuideBody) => createGuide(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['guides'] });
    },
  });
};

export const usePatchGuide = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateGuideBody) => patchGuideApi(id, body),
    onSuccess: (guide) => {
      qc.invalidateQueries({ queryKey: ['guides'] });
      qc.invalidateQueries({ queryKey: ['guide', guide.slug] });
      qc.invalidateQueries({ queryKey: ['guide', guide.id] });
    },
  });
};

export const useDeleteGuide = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteGuideApi(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['guides'] });
    },
  });
};

// ── Checklists ───────────────────────────────────────────────

export const useChecklistTemplates = (domain?: string) =>
  useQuery({
    queryKey: ['checklist-templates', domain ?? 'all'],
    queryFn: () => fetchChecklistTemplates(domain),
    staleTime: 5 * 60_000,
  });

export const useChecklistTemplate = (idOrSlug: string | undefined) =>
  useQuery({
    queryKey: ['checklist-template', idOrSlug],
    queryFn: () => fetchChecklistTemplate(idOrSlug!),
    enabled: !!idOrSlug,
    staleTime: 5 * 60_000,
  });

export const useChecklistRuns = (filter: ChecklistRunsFilter = {}) =>
  useQuery({
    queryKey: ['checklist-runs', filter] as const,
    queryFn: () => fetchChecklistRuns(filter),
    staleTime: 30_000,
  });

export const useChecklistRun = (id: string | undefined) =>
  useQuery({
    queryKey: ['checklist-run', id],
    queryFn: () => fetchChecklistRun(id!),
    enabled: !!id,
    staleTime: 10_000,
  });

export function useCreateChecklistRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRunBody) => createChecklistRun(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-runs'] });
    },
  });
}

export function useCreateChecklistTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTemplateBody) => createChecklistTemplate(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-templates'] });
    },
  });
}

export function usePatchChecklistTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateTemplateBody) => patchChecklistTemplate(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-templates'] });
      qc.invalidateQueries({ queryKey: ['checklist-template'] });
    },
  });
}

export function useDeleteChecklistTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteChecklistTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['checklist-templates'] });
    },
  });
}

export function useDeleteChecklistRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteChecklistRun(id),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: ['checklist-run', id] });
      qc.invalidateQueries({ queryKey: ['checklist-runs'] });
    },
  });
}

export function usePatchChecklistRun(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { status?: ChecklistRunStatus; notes?: string | null; title?: string }) =>
      patchChecklistRun(runId, patch),
    onSuccess: (data) => {
      qc.setQueryData(['checklist-run', runId], data satisfies ChecklistRunWithItems);
      qc.invalidateQueries({ queryKey: ['checklist-runs'] });
    },
  });
}

export function usePatchChecklistRunItem(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      itemId,
      patch,
    }: {
      itemId: string;
      patch: { status?: ChecklistRunItemStatus; notes?: string | null };
    }) => patchChecklistRunItem(itemId, patch),
    // Optimistic update — the dominant interaction in field use is
    // tap-to-toggle, and the indicator should flip instantly even on
    // a 4G one-bar connection.
    onMutate: async ({ itemId, patch }) => {
      await qc.cancelQueries({ queryKey: ['checklist-run', runId] });
      const previous = qc.getQueryData<ChecklistRunWithItems>(['checklist-run', runId]);
      if (previous) {
        qc.setQueryData<ChecklistRunWithItems>(['checklist-run', runId], {
          ...previous,
          items: previous.items.map((i) =>
            i.id === itemId
              ? {
                  ...i,
                  status: patch.status ?? i.status,
                  notes: patch.notes === undefined ? i.notes : patch.notes,
                }
              : i,
          ),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['checklist-run', runId], ctx.previous);
    },
    onSettled: (data) => {
      // The server response is authoritative — overwrite the
      // optimistic copy with the real `run` (which may have flipped
      // to in_progress).
      if (data) {
        const previous = qc.getQueryData<ChecklistRunWithItems>(['checklist-run', runId]);
        if (previous) {
          qc.setQueryData<ChecklistRunWithItems>(['checklist-run', runId], {
            ...previous,
            run: data.run,
            items: previous.items.map((i) => (i.id === data.item.id ? data.item : i)),
          });
        }
      }
      qc.invalidateQueries({ queryKey: ['checklist-runs'] });
    },
  });
}

// Re-export the row types so consumers don't import them from
// two places.
export type { ChecklistRun, ChecklistRunItem };

// ── Maintenance logs ─────────────────────────────────────────

export const useLogs = (filter: LogsFilter = {}) =>
  useQuery({
    queryKey: ['logs', filter] as const,
    queryFn: () => fetchLogs(filter),
    staleTime: 30_000,
  });

export const useLog = (id: string | undefined) =>
  useQuery({
    queryKey: ['log', id],
    queryFn: () => fetchLog(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

export function useCreateLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateLogBody) => createLog(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['logs'] });
    },
  });
}

export function usePatchLog(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<CreateLogBody>) => patchLog(id, patch),
    onSuccess: (data) => {
      qc.setQueryData(['log', id], data satisfies MaintenanceLog);
      qc.invalidateQueries({ queryKey: ['logs'] });
    },
  });
}

export function useDeleteLog() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteLog(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['logs'] });
    },
  });
}

// ── Assets / photos ──────────────────────────────────────────

export const useAssets = (filter: AssetsFilter = {}) =>
  useQuery({
    queryKey: ['assets', filter] as const,
    queryFn: () => fetchAssets(filter),
    staleTime: 30_000,
  });

export const useAsset = (id: string | undefined) =>
  useQuery({
    queryKey: ['asset', id],
    queryFn: () => fetchAsset(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

export function useUploadAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ file, meta }: { file: File; meta: UploadAssetMeta }) => uploadAsset(file, meta),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function usePatchAsset(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: { caption?: string | null; takenAt?: string | null; tags?: string[] }) =>
      patchAsset(id, patch),
    onSuccess: (data) => {
      qc.setQueryData(['asset', id], data satisfies AssetWithTags);
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

export function useDeleteAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteAsset(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assets'] });
    },
  });
}

// ── Equipment ────────────────────────────────────────────────

export const useEquipmentList = () =>
  useQuery({ queryKey: ['equipment'], queryFn: fetchEquipmentList, staleTime: 60_000 });

export const useEquipment = (id: string | undefined) =>
  useQuery({
    queryKey: ['equipment-detail', id],
    queryFn: () => fetchEquipment(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

export function useCreateEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateEquipmentBody) => createEquipment(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
  });
}

export function usePatchEquipment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<CreateEquipmentBody>) => patchEquipment(id, patch),
    onSuccess: (data) => {
      qc.setQueryData(['equipment-detail', id], data satisfies EquipmentDetail);
      qc.invalidateQueries({ queryKey: ['equipment'] });
    },
  });
}

export function useDeleteEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteEquipment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment'] }),
  });
}

export function useAddEquipmentPart(equipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreatePartBody) => addEquipmentPart(equipmentId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment-detail', equipmentId] }),
  });
}

export function usePatchEquipmentPart(equipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ partId, patch }: { partId: string; patch: Partial<CreatePartBody> }) =>
      patchEquipmentPart(equipmentId, partId, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment-detail', equipmentId] }),
  });
}

export function useDeleteEquipmentPart(equipmentId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (partId: string) => deleteEquipmentPart(equipmentId, partId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['equipment-detail', equipmentId] }),
  });
}

// ── Tools / Consumables / Kits ───────────────────────────────

export const useTools = () =>
  useQuery({ queryKey: ['tools'], queryFn: fetchTools, staleTime: 60_000 });

export function useCreateTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateToolBody) => createTool(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tools'] }),
  });
}
export function usePatchTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CreateToolBody> }) => patchTool(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tools'] }),
  });
}
export function useDeleteTool() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteTool(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tools'] }),
  });
}

export const useConsumables = () =>
  useQuery({ queryKey: ['consumables'], queryFn: fetchConsumables, staleTime: 60_000 });

export const useLowStock = () =>
  useQuery({ queryKey: ['consumables', 'low-stock'], queryFn: fetchLowStock, staleTime: 60_000 });

export function useCreateConsumable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateConsumableBody) => createConsumable(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consumables'] }),
  });
}
export function usePatchConsumable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CreateConsumableBody> }) =>
      patchConsumable(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consumables'] }),
  });
}
export function useDeleteConsumable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteConsumable(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['consumables'] }),
  });
}

export const useKits = () =>
  useQuery({ queryKey: ['kits'], queryFn: fetchKits, staleTime: 60_000 });
export const useKit = (id: string | undefined) =>
  useQuery({
    queryKey: ['kit', id],
    queryFn: () => fetchKit(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
export function useCreateKit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateKitBody) => createKit(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kits'] }),
  });
}
export function useDeleteKit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteKit(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kits'] }),
  });
}
export function useAddKitItem(kitId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { itemType: KitItemType; itemId: string; quantity?: number | null; notes?: string | null }) =>
      addKitItemApi(kitId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kit', kitId] }),
  });
}
export function useDeleteKitItem(kitId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => deleteKitItem(kitId, itemId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kit', kitId] }),
  });
}

// Re-export inventory row types so consumers can stick to one import.
export type { Consumable, Kit, KitItemRow, KitWithItems, Tool };

// ── Projects ─────────────────────────────────────────────────

export const useProjects = (filter: ProjectsFilter = {}) =>
  useQuery({
    queryKey: ['projects', filter] as const,
    queryFn: () => fetchProjects(filter),
    staleTime: 30_000,
  });

export const useProject = (id: string | undefined) =>
  useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

export function useCreateProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateProjectBody) => createProject(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export function usePatchProject(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<CreateProjectBody>) => patchProject(id, patch),
    onSuccess: (data) => {
      qc.setQueryData(['project', id], data satisfies Project);
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
}

export function useDeleteProject() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteProject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
}

export type { Project };

// ── Vendors ──────────────────────────────────────────────────

export const useVendors = () =>
  useQuery({ queryKey: ['vendors'], queryFn: fetchVendors, staleTime: 60_000 });

export function useCreateVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateVendorBody) => createVendor(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}
export function usePatchVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<CreateVendorBody> }) =>
      patchVendor(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}
export function useDeleteVendor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteVendor(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['vendors'] }),
  });
}

// ── References ───────────────────────────────────────────────

export const useReferences = (domainId?: string) =>
  useQuery({
    queryKey: ['references', domainId ?? 'all'] as const,
    queryFn: () => fetchReferences(domainId),
    staleTime: 60_000,
  });

export function useCreateReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateReferenceBody) => createReference(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['references'] }),
  });
}
export function useDeleteReference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteReference(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['references'] }),
  });
}

export type { Reference, Vendor };
